/**
 * 掲示板本文メディアの Firebase Storage アップロードヘルパ。
 * 仕様書: docs/02_work/done/plan_board_feature.md §3-2 (storage) / §7-3・§7-4 (メディア)
 *
 * - 画像: 5MB 以下ならオリジナルをそのままアップロード（PNG/GIF の透過を保持）。
 *   超過時のみ canvas でリサイズ + JPEG 圧縮して 5MB 以下へ収める。
 * - 動画: 100MB 上限。uploadBytesResumable で進捗を親へ通知しながらアップロード。
 * - 返り値はいずれも getDownloadURL の https ダウンロード URL（本文 HTML に埋め込む）。
 */
import {
  getDownloadURL,
  ref as storageRef,
  uploadBytes,
  uploadBytesResumable,
} from 'firebase/storage';

import { storage } from '@/lib/firebase';
import { resizeImage } from '@/lib/imageResize';

/** 画像はオリジナル保持の上限（超えたらリサイズ + JPEG 圧縮）。 */
const IMAGE_ORIGINAL_MAX_BYTES = 5 * 1024 * 1024;
/** リサイズ時の長辺上限。 */
const IMAGE_MAX_DIMENSION = 2048;
/** 動画の上限サイズ。 */
const VIDEO_MAX_BYTES = 100 * 1024 * 1024;
/** 添付ファイル（成果物）の上限サイズ。形式は問わない。 */
const ATTACHMENT_MAX_BYTES = 100 * 1024 * 1024;

/** ファイル名の拡張子を小文字で取得（無ければ空文字）。 */
function extOf(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  return dot >= 0 ? fileName.slice(dot + 1).toLowerCase() : '';
}

/**
 * 掲示板本文画像をアップロードして https ダウンロード URL を返す。
 * - 5MB 以下: オリジナルをそのままアップロード（透過 PNG/GIF を保持）。
 * - 5MB 超過: 長辺 2048px / 5MB 以下へリサイズ + JPEG 圧縮してアップロード。
 * パス: board/imgs/{postId}/{timestamp}_{rand}.{ext}
 */
export async function uploadBoardImage(
  postId: string,
  file: File,
): Promise<string> {
  let blob: Blob = file;
  let ext = extOf(file.name) || 'jpg';
  let contentType = file.type || `image/${ext}`;

  if (file.size > IMAGE_ORIGINAL_MAX_BYTES) {
    const resized = await resizeImage(file, {
      maxDimension: IMAGE_MAX_DIMENSION,
      maxBytes: IMAGE_ORIGINAL_MAX_BYTES,
    });
    blob = resized.blob;
    ext = resized.ext;
    contentType = resized.blob.type || `image/${ext}`;
  }

  const fileName = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const targetRef = storageRef(storage, `board/imgs/${postId}/${fileName}`);
  await uploadBytes(targetRef, blob, { contentType });
  return getDownloadURL(targetRef);
}

/**
 * 掲示板の添付ファイル（成果物）を Storage へアップロードし、**storagePath** を返す。
 *
 * ★ 本文メディアと違い getDownloadURL は呼ばない（恒久 URL を作らないため）。
 *   ダウンロードは resolveBoardAttachmentDownload が発行する署名付き URL 経由。
 * ★ 形式は問わない（pdf/zip/mov/mp3/… 何でも可）。上限は 100MB。
 * ★ パスに uid を含める: board/files/{postId}/{uid}/{timestamp}_{rand}_{safeName}
 *   storage.rules とサーバ側の両方で「自分の uid 配下のみ」を強制するため。
 *   これがないと、他人がアップロードしたファイルのパスを自分の添付として
 *   登録し直して保護を外す／削除する経路が残る。
 */
export async function uploadBoardAttachment(
  postId: string,
  uid: string,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<string> {
  if (file.size > ATTACHMENT_MAX_BYTES) {
    throw new Error(
      `100MB以下のファイルを選択してください（現 ${(file.size / 1024 / 1024).toFixed(1)} MB）`,
    );
  }
  if (file.size === 0) {
    throw new Error('空のファイルはアップロードできません');
  }

  const safeName = file.name.replace(/[^\w.\-ぁ-んァ-ヶ一-龯]/g, '_').slice(0, 120);
  const rand = Math.random().toString(36).slice(2, 8);
  const storagePath = `board/files/${postId}/${uid}/${Date.now()}_${rand}_${safeName}`;
  const fileRef = storageRef(storage, storagePath);

  const task = uploadBytesResumable(fileRef, file, {
    contentType: file.type || 'application/octet-stream',
  });

  return new Promise<string>((resolve, reject) => {
    task.on(
      'state_changed',
      (snapshot) => {
        onProgress?.(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100));
      },
      (err) => reject(err),
      () => resolve(storagePath),
    );
  });
}

/**
 * 掲示板本文動画をアップロードして https ダウンロード URL を返す。
 * - 100MB を超える場合は例外を投げる（呼び出し側で toast 表示）。
 * - onProgress(0-100) で進捗を通知する（uploadBytesResumable）。
 * パス: board/videos/{postId}/{timestamp}-{safeName}
 */
export async function uploadBoardVideo(
  postId: string,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<string> {
  if (file.size > VIDEO_MAX_BYTES) {
    throw new Error(
      `100MB以下の動画を選択してください（現 ${(file.size / 1024 / 1024).toFixed(1)} MB）`,
    );
  }

  const safeName = file.name.replace(/[^\w.\-ぁ-んァ-ヶ一-龯]/g, '_');
  const storagePath = `board/videos/${postId}/${Date.now()}-${safeName}`;
  const fileRef = storageRef(storage, storagePath);

  // storage.rules は contentType が video/* であることを要求する。
  // 一部コンテナ (.mkv 等) はブラウザが application/octet-stream 等を返すため、
  // video/* でなければ video/mp4 に正規化してアップロード拒否を防ぐ。
  const contentType = /^video\//.test(file.type) ? file.type : 'video/mp4';

  const task = uploadBytesResumable(fileRef, file, { contentType });

  return new Promise<string>((resolve, reject) => {
    task.on(
      'state_changed',
      (snapshot) => {
        const pct = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100,
        );
        onProgress?.(pct);
      },
      (err) => reject(err),
      () => {
        void getDownloadURL(fileRef).then(resolve).catch(reject);
      },
    );
  });
}
